import React, { useEffect, useState } from "react";
import { Bell, Download, Globe2, KeyRound, Save, ShieldCheck, UserCircle } from "lucide-react";
import { toast } from "sonner";
import { TESTIDS } from "@/constants/testIds";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SUPPORTED_LANGS, useI18n } from "@/lib/i18n";

const LANGUAGE_LABELS = { en: "English", it: "Italiano", de: "Deutsch", es: "Español", zh: "简体中文", ru: "Русский" };
const DEFAULT_PREFERENCES = { deal_updates: true, document_updates: true, product_updates: false };

function errorDetail(error, fallback) {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
}

function Section({ icon: Icon, title, help, children }) {
  return (
    <section className="border border-[var(--td-border)] bg-[var(--td-surface)] p-6 md:p-8">
      <div className="flex items-start gap-3 mb-6">
        <div className="mt-0.5 text-[var(--td-gold)]"><Icon size={18} /></div>
        <div>
          <h2 className="font-serif text-2xl">{title}</h2>
          {help && <p className="mt-1 text-sm text-[var(--td-text-2)]">{help}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function NotificationOption({ testid, checked, onChange, title, help }) {
  return (
    <label className="flex items-start gap-4 py-4 border-b border-[var(--td-border)] last:border-b-0 cursor-pointer">
      <input type="checkbox" className="mt-1 accent-[var(--td-gold)]" data-testid={testid}
        checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>
        <span className="block text-sm text-[var(--td-text)]">{title}</span>
        <span className="block mt-1 text-xs text-[var(--td-text-3)]">{help}</span>
      </span>
    </label>
  );
}

export default function SettingsPage() {
  const { t, lang, setLang } = useI18n();
  const { user, updateCurrentUser } = useAuth();
  const [profile, setProfile] = useState({ full_name: "", company: "" });
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setProfile({ full_name: user?.full_name || "", company: user?.company || "" });
  }, [user]);

  useEffect(() => {
    api.getPreferences()
      .then((saved) => setPreferences({ ...DEFAULT_PREFERENCES, ...saved }))
      .catch(() => {});
  }, []);

  const saveProfile = async (event) => {
    event.preventDefault();
    setSavingProfile(true);
    try {
      const updated = await api.updateProfile(profile);
      updateCurrentUser(updated);
      toast.success(t("settings_profile_saved"));
    } catch (error) {
      toast.error(errorDetail(error, t("settings_profile_failed")));
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error(t("settings_password_mismatch"));
      return;
    }
    setSavingPassword(true);
    try {
      await api.changePassword({
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      setPasswords({ current_password: "", new_password: "", confirm_password: "" });
      toast.success(t("settings_password_changed"));
    } catch (error) {
      toast.error(errorDetail(error, t("settings_password_failed")));
    } finally {
      setSavingPassword(false);
    }
  };

  const savePreferences = async (event) => {
    event.preventDefault();
    setSavingPreferences(true);
    try {
      const saved = await api.updatePreferences(preferences);
      setPreferences(saved);
      toast.success(t("settings_notifications_saved"));
    } catch (error) {
      toast.error(errorDetail(error, t("settings_notifications_failed")));
    } finally {
      setSavingPreferences(false);
    }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const data = await api.exportAccountData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `tradedocs-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success(t("settings_exported"));
    } catch (error) {
      toast.error(errorDetail(error, t("settings_export_failed")));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12 max-w-4xl">
      <div className="mb-10">
        <div className="kicker mb-3">— {t("d_nav_settings")}</div>
        <h1 className="font-serif text-4xl md:text-5xl mb-3">{t("d_nav_settings")}</h1>
        <p className="text-[var(--td-text-2)] max-w-2xl leading-relaxed">{t("settings_intro")}</p>
      </div>

      <div className="space-y-6">
        <Section icon={UserCircle} title={t("settings_profile")} help={t("settings_profile_help")}>
          <form data-testid={TESTIDS.settingsProfileForm} onSubmit={saveProfile} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="td-label">{t("settings_full_name")}</label>
                <input required minLength={2} maxLength={120} className="td-input"
                  data-testid={TESTIDS.settingsFullName} value={profile.full_name}
                  onChange={(e) => setProfile((value) => ({ ...value, full_name: e.target.value }))} />
              </div>
              <div>
                <label className="td-label">{t("settings_company")}</label>
                <input maxLength={200} className="td-input" data-testid={TESTIDS.settingsCompany}
                  value={profile.company}
                  onChange={(e) => setProfile((value) => ({ ...value, company: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="td-label">{t("settings_email")}</label>
              <input disabled className="td-input opacity-70" value={user?.email || ""} />
              <div className="mt-1 text-xs text-[var(--td-text-3)]">{t("settings_email_help")}</div>
            </div>
            <button type="submit" disabled={savingProfile} data-testid={TESTIDS.settingsProfileSave}
              className="btn-gold inline-flex items-center gap-2 disabled:opacity-50">
              <Save size={14} /> {t("settings_save_profile")}
            </button>
          </form>
        </Section>

        <Section icon={Globe2} title={t("settings_language")} help={t("settings_language_help")}>
          <select className="td-input max-w-sm" value={lang} data-testid={TESTIDS.settingsLanguage}
            onChange={(e) => setLang(e.target.value)}>
            {SUPPORTED_LANGS.map((code) => <option key={code} value={code}>{LANGUAGE_LABELS[code]}</option>)}
          </select>
        </Section>

        <Section icon={Bell} title={t("settings_notifications")} help={t("settings_notifications_help")}>
          <form data-testid={TESTIDS.settingsNotificationsForm} onSubmit={savePreferences}>
            <NotificationOption testid={TESTIDS.settingsDealUpdates}
              checked={preferences.deal_updates}
              onChange={(checked) => setPreferences((value) => ({ ...value, deal_updates: checked }))}
              title={t("settings_deal_updates")} help={t("settings_deal_updates_help")} />
            <NotificationOption testid={TESTIDS.settingsDocumentUpdates}
              checked={preferences.document_updates}
              onChange={(checked) => setPreferences((value) => ({ ...value, document_updates: checked }))}
              title={t("settings_document_updates")} help={t("settings_document_updates_help")} />
            <NotificationOption testid={TESTIDS.settingsProductUpdates}
              checked={preferences.product_updates}
              onChange={(checked) => setPreferences((value) => ({ ...value, product_updates: checked }))}
              title={t("settings_product_updates")} help={t("settings_product_updates_help")} />
            <div className="mt-4 text-xs text-[var(--td-text-3)]">{t("settings_security_alerts")}</div>
            <button type="submit" disabled={savingPreferences} data-testid={TESTIDS.settingsNotificationsSave}
              className="btn-gold mt-5 inline-flex items-center gap-2 disabled:opacity-50">
              <Save size={14} /> {t("settings_save_notifications")}
            </button>
          </form>
        </Section>

        <Section icon={KeyRound} title={t("settings_security")} help={t("settings_security_help")}>
          <form data-testid={TESTIDS.settingsPasswordForm} onSubmit={savePassword} className="space-y-5">
            <div>
              <label className="td-label">{t("settings_current_password")}</label>
              <input required type="password" autoComplete="current-password" className="td-input max-w-lg"
                data-testid={TESTIDS.settingsCurrentPassword} value={passwords.current_password}
                onChange={(e) => setPasswords((value) => ({ ...value, current_password: e.target.value }))} />
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="td-label">{t("settings_new_password")}</label>
                <input required type="password" minLength={12} maxLength={128} autoComplete="new-password" className="td-input"
                  data-testid={TESTIDS.settingsNewPassword} value={passwords.new_password}
                  onChange={(e) => setPasswords((value) => ({ ...value, new_password: e.target.value }))} />
              </div>
              <div>
                <label className="td-label">{t("settings_confirm_password")}</label>
                <input required type="password" minLength={12} maxLength={128} autoComplete="new-password" className="td-input"
                  data-testid={TESTIDS.settingsConfirmPassword} value={passwords.confirm_password}
                  onChange={(e) => setPasswords((value) => ({ ...value, confirm_password: e.target.value }))} />
              </div>
            </div>
            <button type="submit" disabled={savingPassword} data-testid={TESTIDS.settingsPasswordSave}
              className="btn-gold inline-flex items-center gap-2 disabled:opacity-50">
              <ShieldCheck size={14} /> {t("settings_change_password")}
            </button>
          </form>
          <div className="mt-6 pt-5 border-t border-[var(--td-border)] text-xs text-[var(--td-text-3)]">
            {t("settings_future_security")}
          </div>
        </Section>

        <Section icon={Download} title={t("settings_data")} help={t("settings_data_help")}>
          <button type="button" onClick={exportData} disabled={exporting} data-testid={TESTIDS.settingsDataExport}
            className="btn-gold inline-flex items-center gap-2 disabled:opacity-50">
            <Download size={14} /> {t("settings_export")}
          </button>
        </Section>
      </div>
    </div>
  );
}
